import {
	getNumberFormatOptions,
	guardNumber,
	type NumberFormatVariant,
} from "@msuite/utils/numbers";
import type { Nullable } from "@msuite/utils/types";
import { Button, I18nProvider, Input, NumberField } from "react-aria-components";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { Icon } from "../icons";
import { cn, Fragment } from "../utils";
import { type ExtendedInputWrapperProps, InputWrapper } from "./input-wrapper";

/** Props Interface */
interface NumberInputProps<T extends FieldValues> extends ExtendedInputWrapperProps<T> {
	control: Control<T>;
	name: Path<T>;
	min?: number;
	max?: number;
	step?: number;
	placeholder?: string | number;
	variant?: Nullable<NumberFormatVariant>;
	defaultValue?: number;
	formatOptions?: Intl.NumberFormatOptions;
	hideButtons?: boolean;
	focusAllOnSelect?: boolean;
	/**
	 * Called with the new value whenever the person changes it — not when the form sets it — so a
	 * field derived from this one can follow without the two updating each other in a loop.
	 */
	onValueChange?: (value: number) => void;
}

export function NumberInput<T extends FieldValues>({
	control,
	name,
	label,
	errorVariant,
	isRequired,
	isDisabled,
	placeholder,
	variant = "integer",
	step,
	defaultValue,
	min,
	max,
	helperText,
	formatOptions = {},
	className,
	hideButtons,
	focusAllOnSelect = true,
	onValueChange,
}: NumberInputProps<T>) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, formState }) => (
				<InputWrapper
					isDisabled={isDisabled}
					isRequired={isRequired}
					name={name}
					label={label}
					errors={formState.errors}
					errorVariant={errorVariant}
					helperText={helperText}
				>
					<I18nProvider locale="de-DE">
						<NumberField
							isDisabled={isDisabled}
							value={field.value}
							onChange={(value) => {
								field.onChange(value);
								onValueChange?.(value);
							}}
							formatOptions={Object.assign(getNumberFormatOptions(variant), formatOptions) || {}}
							step={step}
							defaultValue={defaultValue}
							minValue={min}
							maxValue={max}
						>
							<div
								className={cn(
									"input h-stack h-9 p-0 items-stretch gap-0 focus-within:input-focused focus-within:*:ring-0! **:tabular-nums",
									className,
								)}
							>
								<Input
									className="h-full flex-1 input border-none bg-none shadow-none"
									placeholder={guardNumber(placeholder).toString()}
									onWheel={(e) => e.preventDefault()}
									onFocus={(e) => {
										if (focusAllOnSelect) e.target.select();
									}}
								/>
								{!hideButtons && (
									<Fragment>
										<Button
											slot="decrement"
											className="h-9 px-3.5 cursor-pointer border-x-[0.5px] border-color"
										>
											<div>
												<Icon name="Minus" />
											</div>
										</Button>
										<Button
											slot="increment"
											className="h-9 px-3.5 cursor-pointer"
										>
											<Icon name="Plus" />
										</Button>
									</Fragment>
								)}
							</div>
						</NumberField>
					</I18nProvider>
				</InputWrapper>
			)}
		/>
	);
}
